import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck, UserPlus, X } from "lucide-react";
import { useRoles, useUserRoles } from "@/api/queries/roles";
import { useAssignRole, useUnassignRole } from "@/api/mutations/roles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionCard } from "@/components/SectionCard";

// #274: Rollen eines Users im User-Detail — sehen, zuweisen, entziehen.
export function UserRolesSection({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useUserRoles(userId);
  const all = useRoles();
  const [pick, setPick] = useState("");
  const assign = useAssignRole();
  const unassign = useUnassignRole();
  // ⚠ #331: der Geltungsbereich ist ersatzlos gestrichen — eine Rolle ist eine GLOBALE Menge
  // von Personen und steht je Person höchstens einmal.
  //
  // ⚠⚠ Trotzdem wird hier DEDUPLIZIERT (Fix-Runde 1, MED-5): ein Master, der die Migration
  // noch nicht gelaufen hat, liefert dieselbe Rolle zweimal (global + ehemals gescopt). Ohne
  // den Riegel rendern zwei Zeilen mit DEMSELBEN `data-testid` — die zweite ist per Klickweg
  // gar nicht erreichbar, und ein Entziehen sieht aus, als hätte es nicht gewirkt.
  const roles = [...new Map((Array.isArray(data) ? data : []).map((r) => [r.id, r] as const)).values()];
  const zugewiesen = new Set(roles.map((r) => r.id));
  const candidates = (Array.isArray(all.data) ? all.data : []).filter((r) => !zugewiesen.has(r.id));

  return (
    <SectionCard
      testId="user-roles-section"
      icon={<BadgeCheck className="h-[18px] w-[18px]" />}
      title={`Rollen (${roles.length})`}
      description="Fach-Rollen benennen Zuständigkeiten; System-Rollen tragen Plattform-Rechte."
      action={
        <Link to="/roles" className="text-[12.5px] text-accent-text hover:underline">
          Rollen verwalten
        </Link>
      }
    >
      {/* Fehler NIE als Leerzustand zeigen (Muster BindingsSection): „keine Rolle" und
          „konnte nicht laden" sind für den Admin zwei verschiedene Aussagen. */}
      {isError ? (
        <div className="flex items-center gap-2 text-[12.5px] text-danger" data-testid="user-roles-error">
          <AlertTriangle className="h-4 w-4" />
          Rollen konnten nicht geladen werden.
        </div>
      ) : isLoading && !data ? (
        <div className="text-[12.5px] text-text3">Lädt…</div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger className="w-[260px]" data-testid="user-role-select">
                <SelectValue placeholder="Rolle wählen…" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={!pick || assign.isPending}
              onClick={() =>
                assign.mutate(
                  { roleId: pick, userId },
                  {
                    onSuccess: () => {
                      toast.success("Rolle zugewiesen");
                      setPick("");
                    },
                    onError: (e) => toast.error(`Zuweisen fehlgeschlagen: ${(e as Error).message}`),
                  },
                )
              }
              data-testid="user-role-assign"
            >
              <UserPlus className="h-4 w-4" />
              Zuweisen
            </Button>
          </div>

          {roles.length === 0 ? (
            <div className="text-[12.5px] text-text3">Diesem User ist keine Rolle zugewiesen.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {roles.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 rounded border border-border px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Link to={`/roles/${r.id}`} className="font-semibold text-text hover:underline">
                      {r.name}
                    </Link>
                    {r.kind === "system" && <Badge variant="default">System</Badge>}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={unassign.isPending}
                    onClick={() =>
                      unassign.mutate(
                        { roleId: r.id, userId },
                        {
                          onSuccess: () => toast.success(`Rolle "${r.name}" entzogen`),
                          onError: (e) => toast.error(`Entziehen fehlgeschlagen: ${(e as Error).message}`),
                        },
                      )
                    }
                    data-testid={`user-role-unassign-${r.id}`}
                  >
                    <X className="h-4 w-4" />
                    Entziehen
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
